import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import {
    Edit3,
    Save,
    X,
    Trash2,
    Calculator,
    Calendar,
    Clock,
    DollarSign,
    Info,
    AlertTriangle,
    CheckCircle2,
    TrendingUp
} from 'lucide-react';
import { COMPREHENSIVE_WAGE_TYPES, getAbsenceWageTypes } from './wageTypesConfig';
import * as payrollApi from '../../../../services/api/payrollApi';
import "react-datepicker/dist/react-datepicker.css";

// Use comprehensive wage types configuration
const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();
const ESSENTIAL_WAGE_TYPES = COMPREHENSIVE_WAGE_TYPES;

// Wage codes that draw from the vacation balance (vacation_balances.used_days).
// Mirror of payrolls/services/VacationBalanceService.VACATION_WAGE_CODES on
// the backend — when the admin is editing one of these rows we fetch the
// employee's current snapshot so they can see remaining days before saving.
const VACATION_WAGE_CODES = new Set(['3220', '3221', '3232', '3235']);

const EditPayslipRowModal = ({ show, handleClose, item, contract, handleEdit, handleDelete, companyId, employeeId }) => {
    const [editedItem, setEditedItem] = useState(item);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showDateFields, setShowDateFields] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [vacationSnapshot, setVacationSnapshot] = useState(null);

    const isVacationRow = !!editedItem?.wageType?.code && VACATION_WAGE_CODES.has(editedItem.wageType.code);

    // Fetch vacation snapshot when modal opens on a vacation row, so the
    // admin sees "X dagar kvar" before changing the quantity. Avoids the
    // common foot-gun of pushing the employee into förskott without
    // realising it.
    useEffect(() => {
        if (!show || !isVacationRow || !companyId || !employeeId) {
            setVacationSnapshot(null);
            return;
        }
        let cancelled = false;
        payrollApi.getVacationBalance(companyId, { employeeId })
            .then(res => {
                if (cancelled) return;
                const snap = res?.data?.balances?.[0]
                    || res?.data?.balance
                    || res?.data
                    || null;
                setVacationSnapshot(snap);
            })
            .catch(() => { if (!cancelled) setVacationSnapshot(null); });
        return () => { cancelled = true; };
    }, [show, isVacationRow, companyId, employeeId]);

    // Helper function to get input configuration for a wage type
    const getInputConfig = (wageTypeCode) => {
        return ESSENTIAL_WAGE_TYPES[wageTypeCode] || {
            unit: null,
            inputType: 'hours',
            defaultQuantity: 1,
            label: 'Antal',
            requiresAbsenceDates: false,
            icon: Calculator,
            color: '#6b7280',
            description: 'Standard löneart'
        };
    };

    // Validation function
    const validateForm = () => {
        const errors = [];

        if (!editedItem?.quantity || editedItem.quantity <= 0) {
            errors.push('Antal måste vara större än 0');
        }

        // NOTE: Rate is calculated from contract, not validated here

        if (showDateFields) {
            if (!editedItem?.absenceStartDate || !editedItem?.absenceEndDate) {
                const config = getInputConfig(editedItem?.wageType?.code);
                errors.push(`${config.absenceType} kräver frånvarodatum enligt svensk lag`);
            } else if (new Date(editedItem.absenceStartDate) > new Date(editedItem.absenceEndDate)) {
                errors.push('Startdatum kan inte vara efter slutdatum');
            }
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    useEffect(() => {
        setEditedItem(item);

        // Check if this wage type requires absence dates
        const requiresAbsenceDates = item?.wageType?.code &&
            ABSENCE_WAGE_TYPES.includes(item.wageType.code);

        setShowDateFields(requiresAbsenceDates);

        // Initialize dates if they exist in the item
        if (item?.absenceStartDate) {
            setStartDate(new Date(item.absenceStartDate));
        }
        if (item?.absenceEndDate) {
            setEndDate(new Date(item.absenceEndDate));
        }
    }, [item]);

    const handleInputChange = (event) => {
        setEditedItem({ ...editedItem, [event.target.name]: event.target.value });
    };

    // Handle date changes
    const handleStartDateChange = (date) => {
        setStartDate(date);
        setEditedItem({ ...editedItem, absenceStartDate: date ? date.toISOString().split('T')[0] : null });
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setEditedItem({ ...editedItem, absenceEndDate: date ? date.toISOString().split('T')[0] : null });
    };

    // Save changes with validation for required fields
    const saveChanges = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            // IMPORTANT: Only send quantity and absence dates
            // Backend will recalculate amount and rate from the employee contract
            // This ensures contract rates are the single source of truth
            const updatedItem = {
                ...editedItem,
                // DO NOT send amount or rate - backend calculates from contract
            };

            console.log('Saving changes (backend will recalculate amount/rate from contract):', updatedItem);
            await handleEdit(updatedItem);
            setValidationErrors([]);
        } catch (error) {
            console.error('Error saving changes:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <style>{`
                .modern-edit-modal .modal-content {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    overflow: hidden;
                }

                .modern-edit-modal .modal-header {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border-bottom: 1px solid #e5e7eb;
                    border-radius: 16px 16px 0 0;
                    padding: 2rem 2rem 1.5rem 2rem;
                }

                .modern-edit-modal .modal-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .modern-edit-modal .modal-body {
                    padding: 2rem;
                    background: #f8fafc;
                }

                .modern-edit-modal .modal-footer {
                    background: white;
                    border-top: 1px solid #e5e7eb;
                    border-radius: 0 0 16px 16px;
                    padding: 1.5rem 2rem;
                    display: flex;
                    gap: 1rem;
                    justify-content: space-between;
                    align-items: center;
                }

                .wage-type-header {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .wage-type-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .wage-type-info h5 {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                }

                .wage-type-code {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    font-weight: 500;
                }

                .wage-type-description {
                    font-size: 0.8125rem;
                    color: #64748b;
                    margin-top: 0.25rem;
                }

                .form-section {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .section-title {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .modern-form-group {
                    margin-bottom: 1.5rem;
                }

                .modern-form-label {
                    display: block;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .modern-form-input {
                    width: 100%;
                    padding: 0.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 0.8125rem;
                    transition: all 0.2s ease;
                    background: white;
                }

                .modern-form-input:focus {
                    outline: none;
                    border-color: var(--bs-primary);
                    box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
                }

                .input-group-modern {
                    display: flex;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 2px solid #e5e7eb;
                }

                .input-group-modern .modern-form-input {
                    border: none;
                    border-radius: 0;
                }

                .input-group-modern .input-addon {
                    background: #f8fafc;
                    border-left: 1px solid #e5e7eb;
                    padding: 0.75rem 1rem;
                    font-size: 0.8125rem;
                    color: #6b7280;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                }

                .total-amount {
                    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
                    border: 1px solid #86efac;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .total-label {
                    font-size: 0.8125rem;
                    color: #166534;
                    font-weight: 500;
                }

                .total-value {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #166534;
                }

                .info-section {
                    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
                    border: 1px solid #86efac;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    display: flex;
                    gap: 1rem;
                }

                .info-icon {
                    color: var(--bs-primary);
                    flex-shrink: 0;
                }

                .info-text {
                    color: #166534;
                    font-size: 0.8125rem;
                    line-height: 1.6;
                }

                .date-section {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .date-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }

                .date-picker-modern {
                    width: 100%;
                    padding: 0.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 0.8125rem;
                    transition: all 0.2s ease;
                }

                .date-picker-modern:focus {
                    outline: none;
                    border-color: var(--bs-primary);
                    box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
                }

                .validation-errors {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1.5rem;
                }

                .error-item {
                    color: #dc2626;
                    font-size: 0.8125rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .error-item:last-child {
                    margin-bottom: 0;
                }

                .modern-btn {
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.8125rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    min-width: 120px;
                    justify-content: center;
                }

                .btn-secondary-modern {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }

                .btn-secondary-modern:hover {
                    background: #e2e8f0;
                    color: #475569;
                }

                .btn-primary-modern {
                    background: var(--bs-primary);
                    color: white;
                }

                .btn-primary-modern:hover:not(:disabled) {
                    background: var(--primary-hover);
                    transform: translateY(-1px);
                }

                .btn-primary-modern:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                .btn-danger-modern {
                    background: #ef4444;
                    color: white;
                }

                .btn-danger-modern:hover:not(:disabled) {
                    background: #dc2626;
                    transform: translateY(-1px);
                }

                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .modern-edit-modal .modal-header,
                    .modern-edit-modal .modal-body,
                    .modern-edit-modal .modal-footer {
                        padding: 1.5rem 1rem;
                    }
                    
                    .date-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .modern-edit-modal .modal-footer {
                        flex-direction: column;
                    }
                    
                    .modern-btn {
                        width: 100%;
                    }
                }
            `}</style>

            <Modal show={show} onHide={handleClose} size="lg" className="modern-edit-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Edit3 size={24} color="var(--bs-primary)" />
                        Redigera löneartikel
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Wage Type Header */}
                    {editedItem && (
                        <div className="wage-type-header">
                            <div
                                className="wage-type-icon"
                                style={{ backgroundColor: getInputConfig(editedItem.wageType.code).color }}
                            >
                                {React.createElement(getInputConfig(editedItem.wageType.code).icon, { size: 24 })}
                            </div>
                            <div className="wage-type-info">
                                <h5>{editedItem.wageType.description}</h5>
                                <div className="wage-type-code">{editedItem.wageType.code}</div>
                                <div className="wage-type-description">
                                    {getInputConfig(editedItem.wageType.code).description}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                        <div className="validation-errors">
                            {validationErrors.map((error, index) => (
                                <div key={index} className="error-item">
                                    <AlertTriangle size={14} />
                                    {error}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Employee Contract Context 
                    {contract && (
                        <div className="contract-context" style={{
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            border: '1px solid #bae6fd',
                            borderRadius: '12px',
                            padding: '1.25rem',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '1rem',
                                color: '#0369a1',
                                fontWeight: '600',
                                fontSize: '0.875rem'
                            }}>
                                <Info size={16} />
                                Anställningsuppgifter
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem',
                                fontSize: '0.875rem'
                            }}>
                                {contract.salary && (
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                            Månadslön
                                        </div>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                            {Math.round(contract.salary).toLocaleString('sv-SE')} kr
                                        </div>
                                    </div>
                                )}
                                {contract.hourlyWage && (
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                            Timlön
                                        </div>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                            {Math.round(contract.hourlyWage).toLocaleString('sv-SE')} kr/tim
                                        </div>
                                    </div>
                                )}
                                {contract.workWeekHours && (
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                            Arbetstimmar/vecka
                                        </div>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                            {contract.workWeekHours} timmar
                                        </div>
                                    </div>
                                )}
                                {contract.compensationType && (
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                            Lönetyp
                                        </div>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                            {contract.compensationType === 'Monthly' ? 'Månadsavlönad' :
                                                contract.compensationType === 'Hourly' ? 'Timavlönad' : contract.compensationType}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {contract.salary && editedItem?.wageType?.code === '1101' && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem',
                                    background: 'white',
                                    borderRadius: '8px',
                                    fontSize: '0.8125rem',
                                    color: '#475569'
                                }}>
                                    <strong>Månadslön:</strong> {Math.round(contract.salary).toLocaleString('sv-SE')} kr
                                </div>
                            )}
                        </div>
                    )}*/}

                    {/* Vacation balance context — only on vacation wage codes */}
                    {isVacationRow && vacationSnapshot && (() => {
                        const earned = Number(vacationSnapshot.earnedDays ?? vacationSnapshot.earned_days ?? 0);
                        const carried = Number(vacationSnapshot.carriedOverDays ?? vacationSnapshot.carried_over_days ?? 0);
                        const saved = Number(vacationSnapshot.savedDays ?? vacationSnapshot.saved_days ?? 0);
                        const used = Number(vacationSnapshot.usedDays ?? vacationSnapshot.used_days ?? 0);
                        const remainingPaid = Math.max(0, earned + carried - used);
                        const advanceTaken = Math.max(0, used - earned - carried);
                        const requested = Number(editedItem?.quantity || 0);
                        const willOverdraw = requested > remainingPaid;
                        const tone = advanceTaken > 0 || willOverdraw ? '#b45309' : '#15803d';
                        const bg = advanceTaken > 0 || willOverdraw ? '#fef3c7' : '#dcfce7';
                        return (
                            <div style={{
                                margin: '0 0 1rem 0',
                                padding: '0.75rem 1rem',
                                background: bg,
                                border: `1px solid ${tone}33`,
                                borderRadius: '8px',
                                fontSize: '0.8125rem',
                                color: tone,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '1rem',
                                alignItems: 'center',
                            }}>
                                <strong>Semestersaldo:</strong>
                                <span>{remainingPaid} dagar kvar</span>
                                <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                    (tjänat {earned} + överförda {carried} − använt {used}
                                    {saved > 0 ? `, sparade ${saved}` : ''})
                                </span>
                                {advanceTaken > 0 && (
                                    <span style={{ fontWeight: 600 }}>
                                        Förskottssemester: {advanceTaken} dagar
                                    </span>
                                )}
                                {willOverdraw && advanceTaken === 0 && (
                                    <span style={{ fontWeight: 600 }}>
                                        ⚠ {requested - remainingPaid} dag(ar) blir förskott
                                    </span>
                                )}
                            </div>
                        );
                    })()}

                    {/* Quantity and Rate Section */}
                    <div className="form-section">
                        <div className="section-title">
                            <Calculator size={16} />
                            Antal och belopp
                        </div>

                        <Row>
                            <Col md={6}>
                                <div className="modern-form-group">
                                    <div className="modern-form-label">
                                        {editedItem ? getInputConfig(editedItem.wageType.code).label : 'Antal'}
                                    </div>
                                    <div className="input-group-modern">
                                        <input
                                            type="number"
                                            className="modern-form-input"
                                            name="quantity"
                                            min="0"
                                            step={editedItem ? (getInputConfig(editedItem.wageType.code).inputType === 'amount' ? '0.01' : '1') : '1'}
                                            value={editedItem ? editedItem.quantity : ''}
                                            onChange={handleInputChange}
                                            placeholder={`Ange ${editedItem ? getInputConfig(editedItem.wageType.code).label.toLowerCase() : 'antal'}`}
                                        />
                                        <div className="input-addon">
                                            {editedItem ? getInputConfig(editedItem.wageType.code).label : 'Antal'}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="modern-form-group">
                                    <div className="modern-form-label">
                                        Belopp per enhet
                                        <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                            (från anställningsavtal)
                                        </span>
                                    </div>
                                    <div className="input-group-modern">
                                        <input
                                            type="number"
                                            className="modern-form-input"
                                            name="rate"
                                            step="0.01"
                                            value={editedItem ? editedItem.rate : ''}
                                            disabled={true}
                                            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                                            placeholder="Beräknas från avtal"
                                        />
                                        <div className="input-addon">SEK</div>
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                        Baseras på anställningsavtalet och kan inte ändras här
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Total Amount Display */}
                        {editedItem && (
                            <div className="total-amount">
                                <div className="total-label">Totalt belopp</div>
                                <div className="total-value">
                                    {Math.round(editedItem.quantity * editedItem.rate).toLocaleString('sv-SE')} kr
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Absence Dates Section */}
                    {showDateFields && (
                        <div className="date-section">
                            {/*
                            <div className="info-section">
                                <Info size={20} className="info-icon" />
                                <div className="info-text">
                                <strong>Viktigt:</strong> Enligt svensk lag måste frånvarodatum anges för {getInputConfig(editedItem?.wageType?.code)?.absenceType || 'denna frånvarotyp'} när detta rapporteras till Skatteverket.
                                </div>
                            </div>
                            */}

                            <div className="section-title">
                                <Calendar size={16} />
                                Frånvarodatum
                            </div>

                            <div className="date-grid">
                                <div>
                                    <div className="modern-form-label">Startdatum</div>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={handleStartDateChange}
                                        className="date-picker-modern"
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText="Välj startdatum"
                                    />
                                </div>
                                {/*<div>
                                    <div className="modern-form-label">Slutdatum</div>
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        className="date-picker-modern"
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText="Välj slutdatum"
                                        minDate={startDate}
                                    />
                                </div>
                                */}
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <button
                            className="modern-btn btn-danger-modern"
                            onClick={() => handleDelete(item)}
                        >
                            <Trash2 size={16} />
                            Ta bort
                        </button>
                        <div className="d-flex gap-2">
                            <button className="modern-btn btn-secondary-modern" onClick={handleClose}>
                                <X size={16} />
                                Avbryt
                            </button>
                            <button
                                className="modern-btn btn-primary-modern"
                                onClick={saveChanges}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="loading-spinner" />
                                        Sparar...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Spara ändringar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditPayslipRowModal;